import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, ListItemText, Menu, MenuItem,
  TextField, Tooltip, Typography
} from '@mui/material';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { SAVED_VIEWS_KEY } from './constants';
import { sanitizeFilters } from './format';
import { DocumentFilters, SavedView } from './types';

const MAX_VIEWS = 20;

/** Виды хранятся отдельно для каждого пользователя: на общем компьютере оператор не видит чужие */
function storageKey(): string {
  try {
    return `${SAVED_VIEWS_KEY}.${localStorage.getItem('userId') || 'anon'}`;
  } catch {
    return SAVED_VIEWS_KEY;
  }
}

function loadViews(): SavedView[] {
  try {
    const key = storageKey();
    let stored = localStorage.getItem(key);
    // виды, сохранённые до разделения по пользователям, переходят к первому вошедшему
    const legacy = localStorage.getItem(SAVED_VIEWS_KEY);
    if (stored === null && legacy !== null && key !== SAVED_VIEWS_KEY) {
      localStorage.setItem(key, legacy);
      localStorage.removeItem(SAVED_VIEWS_KEY);
      stored = legacy;
    }
    const raw = JSON.parse(stored || '[]');
    if (!Array.isArray(raw)) return [];
    return raw
      .filter(v => v && typeof v.id === 'string' && typeof v.name === 'string')
      .map(v => ({ id: v.id, name: v.name, filters: sanitizeFilters(v.filters) }));
  } catch {
    return [];
  }
}

function persistViews(views: SavedView[]) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(views));
  } catch {
    /* хранилище недоступно - вид останется только до перезагрузки */
  }
}

interface Props {
  filters: DocumentFilters;
  canSave: boolean;
  onApply: (filters: DocumentFilters) => void;
}

/** Именованные наборы фильтров («Мои виды»): хранятся в браузере пользователя */
const SavedViewsMenu: React.FC<Props> = ({ filters, canSave, onApply }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [views, setViews] = useState<SavedView[]>([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => setViews(loadViews()), []);

  const save = () => {
    const title = name.trim().slice(0, 40);
    if (!title) return;
    // вид с тем же названием заменяется и поднимается наверх
    const others = views.filter(v => v.name.toLowerCase() !== title.toLowerCase());
    const next = [{ id: String(Date.now()), name: title, filters }, ...others].slice(0, MAX_VIEWS);
    setViews(next);
    persistViews(next);
    setNaming(false);
    setName('');
  };

  const remove = (id: string) => {
    const next = views.filter(v => v.id !== id);
    setViews(next);
    persistViews(next);
  };

  return (
    <>
      <Tooltip title="Сохранённые фильтры">
        <Button
          size="small" variant="outlined" color="inherit" onClick={e => setAnchor(e.currentTarget)}
          startIcon={<BookmarkBorderRoundedIcon fontSize="small" />}
          sx={{ textTransform: 'none', borderColor: '#e2e8f0', color: '#334155', height: 36 }}
        >
          Виды{views.length ? ` (${views.length})` : ''}
        </Button>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} slotProps={{ paper: { sx: { minWidth: 260 } } }}>
        {views.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1 }}>
            Сохранённых видов пока нет
          </Typography>
        )}
        {views.map(v => (
          <MenuItem key={v.id} onClick={() => { onApply(v.filters); setAnchor(null); }} sx={{ pr: 1 }}>
            <ListItemText primary={v.name} primaryTypographyProps={{ fontSize: 14 }} />
            <IconButton
              size="small" edge="end" aria-label="Удалить вид"
              onClick={e => { e.stopPropagation(); remove(v.id); }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </MenuItem>
        ))}
        <Box sx={{ borderTop: views.length ? '1px solid #e2e8f0' : 'none', mt: views.length ? 0.5 : 0 }}>
          <Tooltip title={canSave ? '' : 'Сначала задайте хотя бы один фильтр'} placement="left">
            <span>
              <MenuItem disabled={!canSave} onClick={() => { setAnchor(null); setNaming(true); }}>
                <ListItemText primary="Сохранить текущие фильтры…" primaryTypographyProps={{ fontSize: 14, color: canSave ? 'primary' : undefined }} />
              </MenuItem>
            </span>
          </Tooltip>
        </Box>
      </Menu>

      <Dialog open={naming} onClose={() => setNaming(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', fontSize: 16, fontWeight: 700 }}>Сохранить вид</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <TextField
            autoFocus fullWidth size="small" label="Название" value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
            inputProps={{ maxLength: 40 }}
            helperText={views.some(v => v.name.toLowerCase() === name.trim().toLowerCase()) ? 'Вид с таким названием будет заменён' : ' '}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNaming(false)} color="inherit">Отмена</Button>
          <Button onClick={save} variant="contained" disableElevation disabled={!name.trim()}>Сохранить</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SavedViewsMenu;
